import { Flex } from "@radix-ui/themes";
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { useLDClient } from "@launchdarkly/react-sdk";
import { IrisCtaLink } from "../../components/IrisCtaLink";
import { isIOS } from "../../components/irisCtaRuntime";
import { SITE_URL } from "../../lib/site";

const TITLE = "Iris";
const DESCRIPTION =
  "Iris is your non-clinical thought partner, available 24/7 on iMessage. Experience support that feels like a breeze.";
const CANONICAL = SITE_URL;

const PRODUCT_NAME = "IrisChat";
const COMPANY_NAME = "Iris Labs";
const SOCIAL_LINKS = [
  "https://www.instagram.com/messageiris",
  "https://www.tiktok.com/@text_iris",
  "https://www.facebook.com/p/Iris-Chat-61590667221162/",
];

const ORGANIZATION_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: COMPANY_NAME,
  alternateName: PRODUCT_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/iris-speech-bubbles-dark.svg`,
  sameAs: SOCIAL_LINKS,
};

export function HomePage() {
  // useLDClient throws during prerendering when LDProvider context doesn't exist
  const ldClient = typeof window !== "undefined" ? useLDClient() : undefined;
  const [isVisible, setIsVisible] = useState(false);
  // Device-aware channel copy: Apple devices reach Iris on iMessage,
  // everyone else over text. Defaults to "over text" until the client-only
  // check runs, matching the reference design's no-JS fallback.
  const [channelText, setChannelText] = useState("over text");

  useEffect(() => {
    // Trigger the reveal on the next animation frame rather than after a
    // fixed timeout. A fixed delay here directly inflates LCP: the CTA text
    // is the page's LCP element, and Chrome doesn't count opacity:0 content
    // as painted, so any delay before the reveal starts is delay added
    // straight onto LCP (worse under throttled/low-end mobile CPUs, where a
    // fixed 140ms became ~600ms of measured "element render delay"). A
    // double rAF still guarantees the initial (hidden) frame paints first so
    // the CSS transition animates instead of being skipped.
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setIsVisible(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, []);

  useEffect(() => {
    if (isIOS()) {
      setChannelText("on iMessage");
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:site_name" content={PRODUCT_NAME} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <script type="application/ld+json">
          {JSON.stringify(ORGANIZATION_JSON_LD)}
        </script>
      </Helmet>
      <Flex
        className="content"
        direction="column"
        align="start"
        justify="end"
        style={{
          flex: 1,
          minHeight: 0,
        }}
      >
        <div
          className={`copy hero-copy-reveal${isVisible ? " is-visible" : ""}`}
        >
          <h1>say hello to iris 💬</h1>
          <h2>talk, reflect, grow: {channelText}</h2>

          <div className="hero-actions">
            <IrisCtaLink
              overridePhone={
                import.meta.env.VITE_PUBLIC_IRIS_HOME_PAGE_PHONE_NUMBER
              }
              className="cta"
              ldClient={ldClient ?? undefined}
            />
          </div>

          <p className="terms">
            By joining, you agree to the <a href="/terms-conditions/">Terms</a>{" "}
            & <a href="/privacy/">Privacy</a>.
          </p>
        </div>
      </Flex>
    </>
  );
}
